import appConfig from '../config/app-config';

/**
 * Exchange
 */
export enum Exchange {
    AUTOSPREADER   = 'AUTOSPREADER',
    B2C2           = 'B2C2',
    BIBOX          = 'BIBOX',
    BINANCE        = 'BINANCE',
    BINANCE_FUT    = 'BINANCE_FUT',
    BINANCE_US     = 'BINANCE_US',
    BITFINEX       = 'BITFINEX',
    BITHUMB        = 'BITHUMB',
    BITMART        = 'BITMART',
    BITMEX         = 'BITMEX',
    BITSTAMP       = 'BITSTAMP',
    COINBASE       = 'COINBASE',
    COINBASE_PRIME = 'COINBASE_PRIME',
    DERIBIT        = 'DERIBIT',
    ECN            = 'ECN',
    FTX            = 'FTX',
    FTX_US         = 'FTX_US',
    GEMINI         = 'GEMINI',
    HITBTC         = 'HITBTC',
    HUOBIPRO       = 'HUOBIPRO',
    HUOBIPRO_FUT   = 'HUOBIPRO_FUT',
    KRAKEN         = 'KRAKEN',
    KRAKEN_FUT     = 'KRAKEN_FUT',
    OKEX           = 'OKEX',
    SMART          = 'SMART',
    TRADIAS        = 'TRADIAS',
    UPBIT          = 'UPBIT',
    VOYAGER        = 'VOYAGER'
}

export const SMART_ACCOUNT_EXCHANGE_API_VALUE: Exchange = 'CUSTOM' as any;

export const EXCHANGE_NAMING: { [exchange in Exchange]: string } = {
    AUTOSPREADER:   'Autospreader',
    B2C2:           'B2C2',
    BIBOX:          'Bibox',
    BINANCE:        'Binance',
    BINANCE_FUT:    'Binance Futures',
    BINANCE_US:     'Binance USA',
    BITFINEX:       'Bitfinex',
    BITHUMB:        'Bithumb',
    BITMART:        'BitMart',
    BITMEX:         'BitMEX',
    BITSTAMP:       'Bitstamp',
    COINBASE:       'Coinbase Pro',
    COINBASE_PRIME: 'Coinbase Prime',
    DERIBIT:        'Deribit',
    ECN:            'Digital ECN',
    FTX:            'FTX',
    FTX_US:         'FTX US',
    GEMINI:         'Gemini',
    HITBTC:         'HitBTC',
    HUOBIPRO:       'Huobi Global',
    HUOBIPRO_FUT:   'Huobi Global',
    KRAKEN:         'Kraken',
    KRAKEN_FUT:     'Kraken Futures',
    OKEX:           'OKEx',
    SMART:          'Smart',
    TRADIAS:        'Tradias',
    UPBIT:          'Upbit',
    VOYAGER:        'Voyager'
};

EXCHANGE_NAMING[SMART_ACCOUNT_EXCHANGE_API_VALUE] = EXCHANGE_NAMING[Exchange.SMART];

export type ExchangeAllowedInAccount =
    typeof Exchange.B2C2
    | Exchange.BINANCE
    | Exchange.BINANCE_FUT
    | Exchange.BINANCE_US
    | Exchange.BITFINEX
    | Exchange.BITMEX
    | Exchange.BITSTAMP
    | Exchange.COINBASE
    | Exchange.COINBASE_PRIME
    | Exchange.DERIBIT
    | Exchange.ECN
    | Exchange.FTX
    | Exchange.FTX_US
    | Exchange.GEMINI
    | Exchange.HUOBIPRO
    | Exchange.KRAKEN
    | Exchange.KRAKEN_FUT
    | Exchange.OKEX
    | Exchange.TRADIAS
    | Exchange.VOYAGER;

export const EXCHANGES_ALLOWED_IN_ACCOUNT: ExchangeAllowedInAccount[] = appConfig.exchangesAllowedInAccount || [
    Exchange.B2C2,
    Exchange.BINANCE,
    Exchange.BINANCE_FUT,
    Exchange.BINANCE_US,
    Exchange.BITFINEX,
    Exchange.BITMEX,
    Exchange.BITSTAMP,
    Exchange.COINBASE,
    Exchange.COINBASE_PRIME,
    Exchange.DERIBIT,
    Exchange.ECN,
    Exchange.FTX,
    Exchange.FTX_US,
    Exchange.GEMINI,
    Exchange.HUOBIPRO,
    Exchange.KRAKEN,
    Exchange.KRAKEN_FUT,
    Exchange.OKEX,
    Exchange.TRADIAS,
    Exchange.VOYAGER
];

export const EXCHANGES_ALLOWED_TO_TRADE: Exchange[] = [
    Exchange.SMART,
    Exchange.AUTOSPREADER,
    ...EXCHANGES_ALLOWED_IN_ACCOUNT
];

export const EXCHANGES_WITH_SPLIT_ACCOUNT: Exchange[] = [
    Exchange.KRAKEN,
    Exchange.OKEX
];

/**
 * Exchanges, that uses private streaming for MD.
 * Should not include specific cases, like {@link EXCHANGES_WITH_PRIVATE_VWAP_MD_ONLY}.
 */
export const EXCHANGES_WITH_PRIVATE_MD: Exchange[] = [
    Exchange.ECN,
    Exchange.AUTOSPREADER
];

/**
 * Exchanges, that doesn't supported by
 * SUBSCRIBE_VWAP_MD ({@link VwapMdSubscriptionSubject})
 * and SUBSCRIBE_PRIVATE_VWAP_MD ({@link PrivateVwapMdSubscriptionSubject}).
 * So VWAP should be calculated on UI side.
 */
export const EXCHANGES_WITHOUT_VWAP_MD: Exchange[] = [
    Exchange.ECN
];

/**
 * Private exchanges, that support MD with VWAP only.
 */
export const EXCHANGES_WITH_PRIVATE_VWAP_MD_ONLY: Exchange[] = [
    Exchange.B2C2,
    Exchange.TRADIAS
];

/**
 * Private exchanges, that support MD with VWAP only, and VWAP sizes are defined by the exchange.
 */
export const EXCHANGES_WITH_PRIVATE_VWAP_MD_ONLY_UNKNOWN_SIZE: Exchange[] = [
    Exchange.TRADIAS
];

/**
 * Some exchanges may measure the size (order qty) in pieces or something similar.
 * It's used for example to hide "Total" value in Trade Ticket and Order Details,
 * since total can't be calculated as qty * price for these exchanges.
 */
export const EXCHANGES_WITH_SPECIFIC_SIZE_UNIT: Exchange[] = [
    Exchange.AUTOSPREADER,
    Exchange.BITMEX,
    Exchange.DERIBIT,
    Exchange.FTX,
    Exchange.FTX_US,
    Exchange.OKEX
];

export const EXCHANGES_SUPPORT_ICEBERG: Exchange[] = [
    Exchange.BINANCE
];

export function isExchangeAllowedToTrade(exchange: Exchange): boolean {
    return EXCHANGES_ALLOWED_TO_TRADE.includes(exchange);
}

export function isExchangeWithSplitAccount(exchange: Exchange): boolean {
    return EXCHANGES_WITH_SPLIT_ACCOUNT.includes(exchange);
}

export function isExchangeWithPrivateVwapMdOnly(exchange: Exchange): boolean {
    return EXCHANGES_WITH_PRIVATE_VWAP_MD_ONLY.includes(exchange);
}

export function isExchangeWithPrivateMd(exchange: Exchange): boolean {
    return EXCHANGES_WITH_PRIVATE_MD.includes(exchange);
}

export function isExchangeWithoutVwapMd(exchange: Exchange): boolean {
    return EXCHANGES_WITHOUT_VWAP_MD.includes(exchange);
}

export function isExchangeWithPrivateVwapMdOnlyUnknownSize(exchange: Exchange): boolean {
    return EXCHANGES_WITH_PRIVATE_VWAP_MD_ONLY_UNKNOWN_SIZE.includes(exchange);
}

export function isExchangeWithSpecificSizeUnit(exchange: Exchange): boolean {
    return EXCHANGES_WITH_SPECIFIC_SIZE_UNIT.includes(exchange);
}

export function isExchangesSupportsIceberg(exchange: Exchange): boolean {
    return EXCHANGES_SUPPORT_ICEBERG.includes(exchange);
}

/**
 * Allows to hide 'Exchange' columns and filters, and do others related tweaks.
 */
export function isSingleExchange(): boolean {
    return EXCHANGES_ALLOWED_IN_ACCOUNT.length === 1;
}
